import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MainPage from './components/MainPage';
import AuthPage from './components/AuthPage';
import utils, { AUTH_EXPIRED_EVENT } from './utils/utils';
import { AuthContext } from './utils/AuthContext';
import { API } from './service/ProductsService';
import { basket } from './utils/basketStore';
import { favorites } from './utils/favoritesStore';
import { useModalFocus } from './utils/useModalFocus';
import LegalPage, { legalRouteFor } from './legal/LegalPage';
import TermsUpdateDialog from './components/TermsUpdateDialog';
import { TERMS_VERSION } from './legal/siteInfo';

// האתר פתוח לכולם (חיפוש, דשבורד, רשימות). התחברות נדרשת לעדכון מחיר, פידבק ורשימת קניות:
// הקומפוננטה קוראת requireLogin(reason, action) (ר' AuthContext), ואם אין משתמש נפתח חלון AuthPage.
// טוקן שפג / משתמש שנחסם → השרת מחזיר code 90 בפעולה כזו → AUTH_EXPIRED_EVENT → מתנתקים ופותחים את חלון הכניסה.
const noticeStyle = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 300 },
  dialog:  { width: '100%', maxWidth: 420, background: '#fff', borderRadius: 14, padding: 22, textAlign: 'center', boxShadow: '0 16px 48px rgba(0,0,0,.25)' },
  text:    { margin: '0 0 16px', fontSize: 16, lineHeight: 1.6 },
  button:  { padding: '10px 24px', fontSize: 15, fontWeight: 700, border: 'none', borderRadius: 10, background: '#1a3f9c', color: '#fff' },
};

// העמודים המשפטיים (/terms, /privacy, /accessibility) - עמוד נפרד, בלי האתר מסביב (ר' legal/LegalPage.jsx)
const legalRoute = legalRouteFor(window.location.pathname);

const ZolBakfarApp = () => {
  useModalFocus();   // נגישות: מיקוד בחלונות קופצים (utils/useModalFocus.js)
  const [user, setUser] = useState(() => utils.getCurrentUser());
  const [authPrompt, setAuthPrompt] = useState(null);   // { reason } - חלון הכניסה פתוח
  const pendingAction = useRef(null);                   // הפעולה שתרוץ אחרי התחברות מוצלחת

  useEffect(() => {
    const onExpired = () => {
      setUser(null);
      basket.reload();
      favorites.reload();
      setAuthPrompt({ reason: 'ההתחברות פגה, נא להתחבר מחדש' });
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  const requireLogin = useCallback((reason, action) => {
    if (utils.getCurrentUser()) {
      if (action) action();
      return;
    }
    pendingAction.current = action || null;
    setAuthPrompt({ reason });
  }, []);

  const closeAuth = useCallback(() => {
    pendingAction.current = null;
    setAuthPrompt(null);
  }, []);

  const handleLogin = (token, loggedUser) => {
    utils.saveLogin(token, loggedUser);
    setUser(utils.getCurrentUser());
    basket.reload();      // רשימת הקניות של המשתמש הזה
    favorites.reload();   // והמועדפים שלו (מהשרת)
    setAuthPrompt(null);
    const action = pendingAction.current;
    pendingAction.current = null;
    if (action) action();
  };

  const logout = useCallback(async () => {
    // מבטל את הטוקן בשרת (spSignOut); מתנתקים מקומית גם אם הקריאה נכשלה
    try { await API.signOut(); } catch (e) { /* ממשיכים בניתוק */ }
    utils.clearLogin();
    setUser(null);
    basket.reload();      // בלי משתמש - בלי רשימה
    favorites.reload();
  }, []);

  const updateUser = useCallback((patch) => {
    utils.updateCurrentUser(patch);
    setUser(utils.getCurrentUser());
  }, []);

  // אחרי מחיקת חשבון (DeleteAccountDialog → spDeleteAccount): הטוקנים כבר נמחקו בשרת - ניתוק מקומי בלי SignOut, והודעה
  const [notice, setNotice] = useState(null);
  const accountDeleted = useCallback(() => {
    utils.clearLogin();
    try { localStorage.removeItem('has-account'); } catch (e) { /* חסום */ }   // AuthPage ייפתח שוב על "הרשמה"
    setUser(null);
    basket.reload();
    favorites.reload();
    setNotice('החשבון שלך נמחק. תודה שהשתמשת בזול בכפר - אפשר להמשיך לחפש ולהשוות מחירים גם בלי חשבון.');
  }, []);

  const auth = useMemo(() => ({ user, requireLogin, logout, updateUser, accountDeleted }),
                       [user, requireLogin, logout, updateUser, accountDeleted]);

  // משתמש מחובר שלא אישר את הגרסה הנוכחית של התנאים (Users.termsVersion) - חייב לאשר או לצאת
  const needsTerms = !!user && (user.termsVersion ?? 0) < TERMS_VERSION;

  if (legalRoute) return <div dir="rtl" lang="he"><LegalPage route={legalRoute} /></div>;

  return (
    <AuthContext.Provider value={auth}>
      <div dir="rtl" lang="he">
        <MainPage />
        {authPrompt && <AuthPage reason={authPrompt.reason} onLogin={handleLogin} onClose={closeAuth} />}
        {notice && (
          <div style={noticeStyle.overlay}>
            <div style={noticeStyle.dialog} role="dialog" aria-modal="true" aria-labelledby="notice-text">
              <p id="notice-text" style={noticeStyle.text}>{notice}</p>
              <button type="button" style={noticeStyle.button} onClick={() => setNotice(null)}>סגירה</button>
            </div>
          </div>
        )}
        {needsTerms && !authPrompt && (
          <TermsUpdateDialog
            firstTime={!user.termsVersion}
            onAccepted={(termsVersion) => updateUser({ termsVersion })}
            onLogout={logout}
          />
        )}
      </div>
    </AuthContext.Provider>
  );
};

export default ZolBakfarApp;
